import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Check } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import FaqList from "../components/FaqList";
import CTABand from "../components/CTABand";
import ServiceIcon from "../components/ServiceIcon";
import { Stagger, StaggerItem } from "../components/Reveal";
import { SERVICES, CONDITIONS, INSURANCES } from "../data/site";
import { getCounty, COUNTIES } from "../data/counties";
import { breadcrumbSchema, faqSchema, serviceSchema } from "../lib/schema";

export default function County() {
  const { slug } = useParams();
  const county = getCounty(slug);
  if (!county) return <Navigate to="/areas-we-serve" replace />;

  const label = `${county.name} County`;
  const path = `/areas-we-serve/${county.slug}`;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Areas We Serve", path: "/areas-we-serve" },
    { name: label, path },
  ];
  const topCities = county.cities.slice(0, 3).join(", ");
  const faqs = [
    {
      question: `Can I do online therapy if I live in ${label}, NJ?`,
      answer: `Yes. NJ Therapy Online works with clients throughout ${label}, including ${topCities}. You just need to be physically located in New Jersey during your session.`,
    },
    {
      question: `Do you take insurance for online therapy in ${label}?`,
      answer: `Yes. We're in-network with ${INSURANCES.slice(0, 5).join(", ")}, and more. We'll verify your benefits before your first session.`,
    },
    {
      question: `Do you offer teen therapy online in ${label}?`,
      answer: `Yes. Teens ages 12–17 across ${label} can meet with a therapist from home — after school, in the evening, or on weekends.`,
    },
    {
      question: `How quickly can I start therapy in ${label}?`,
      answer: `Reach out through our contact form or call us. We'll verify your insurance and help you get started quickly.`,
    },
  ];
  const neighbors = county.neighbors.map(getCounty).filter(Boolean);
  const description = `Online therapy in ${label}, NJ — serving ${county.cities.slice(0, 4).join(", ")} and more. In-network virtual counseling for anxiety, depression, teens, couples and families.`;

  return (
    <>
      <SEO
        title={`Online Therapy in ${label}, NJ | Virtual Counseling`}
        description={description}
        path={path}
        schema={[
          breadcrumbSchema(crumbs),
          serviceSchema({ name: `Online therapy in ${label}, NJ`, description, path, areaName: `${label}, NJ` }),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow={`${county.region} · County seat: ${county.seat}`}
        title="Online Therapy in"
        accent={`${label}, NJ`}
        body={county.intro}
        testid="county-hero"
      >
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Link to={`/contact?county=${county.slug}`} className="btn btn-primary" data-testid="county-cta">
            Book a Session <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/services" className="btn btn-secondary">View services</Link>
        </div>
      </PageHero>

      <section className="section" data-testid="county-services">
        <div className="container-site grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="text-2xl md:text-3xl">Virtual therapy services in {label}</h2>
            <Stagger stagger={0.05} className="mt-6 grid sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <StaggerItem key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="card card-hover flex items-center gap-3 p-4"
                  data-testid={`county-service-${s.slug}`}
                >
                  <span className="icon-pop grid h-9 w-9 place-items-center rounded-lg bg-[var(--sage-tint)] text-[var(--sage)] shrink-0">
                    <ServiceIcon name={s.icon} className="h-4 w-4" />
                  </span>
                  <span className="text-[15px] font-medium text-[var(--ink)]">Online {s.title}</span>
                </Link>
                </StaggerItem>
              ))}
            </Stagger>
            <div className="mt-6 flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <Link key={c.slug} to={`/conditions/${c.slug}`} className="chip">
                  {c.title} therapy
                </Link>
              ))}
            </div>
          </div>
          <aside className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl bg-[var(--sage-tint)] p-6">
              <h3 className="text-base">Why {label} clients choose online therapy</h3>
              <p className="mt-2 text-sm leading-relaxed text-body">{county.localNote}</p>
              <ul className="mt-4 space-y-2 text-sm text-[var(--ink)]">
                {["In-network with major insurance", "Weeknights until 9 PM & weekends", "Secure, HIPAA-compliant video"].map((t) => (
                  <li key={t} className="flex gap-2">
                    <Check className="h-4 w-4 text-[var(--sage)] mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="section bg-white border-y border-[var(--line)]" data-testid="county-cities">
        <div className="container-site">
          <h2 className="text-2xl md:text-3xl">Cities & towns we serve in {label}</h2>
          <p className="mt-3 text-body">Online therapy is available to residents of every {label} community, including:</p>
          <Stagger as="ul" stagger={0.03} className="mt-6 flex flex-wrap gap-2">
            {county.cities.map((city) => (
              <StaggerItem as="li" key={city} className="chip" data-testid="county-city">
                <MapPin className="h-3.5 w-3.5 text-[var(--sage)]" /> {city}, NJ
              </StaggerItem>
            ))}
          </Stagger>
          {neighbors.length > 0 && (
            <div className="mt-10">
              <h3 className="text-base">Nearby counties</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {neighbors.map((n) => (
                  <Link key={n.slug} to={`/areas-we-serve/${n.slug}`} className="chip" data-testid={`county-neighbor-${n.slug}`}>
                    {n.name} County
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="section" data-testid="county-faq">
        <div className="container-site grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <h2 className="text-2xl md:text-3xl">{label} FAQ</h2>
            <p className="mt-3 text-sm text-body">
              Serving {COUNTIES.length} counties statewide. <Link to="/faq" className="text-[var(--sage)] font-semibold">All FAQs</Link>
            </p>
          </div>
          <div className="lg:col-span-8">
            <FaqList faqs={faqs} testid="county-faq-list" />
          </div>
        </div>
      </section>

      <CTABand title={`Start online therapy in ${label}`} to={`/contact?county=${county.slug}`} />
    </>
  );
}
